import { useState, ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import wordmark from '../assets/brand/wordmark-light.png';
import './Landing.css';

const DEMO_EMAIL = 'zengdigital8@gmail.com';

const expectations = [
  { num: '01', title: '30 minutes, tops', text: 'A live walkthrough of Porrada on web and mobile — no slide decks.' },
  { num: '02', title: 'Your gym, not ours', text: "We'll mirror your setup: your locations, class types and schedule, so you see your own week in the app." },
  { num: '03', title: 'The full loop', text: 'Bookings, self check-in, the live roster, payments and the owner dashboard — end to end.' },
  { num: '04', title: 'Straight answers', text: 'Pricing, migration, feature requests — ask anything, get a real answer.' },
];

export default function BookDemo() {
  const [form, setForm] = useState({ name: '', email: '', gym: '', phone: '', preferred: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const set = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const subject = `Demo request — ${form.gym || form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Gym / studio: ${form.gym}`,
      form.phone && `Phone: ${form.phone}`,
      form.preferred && `Preferred day & time: ${form.preferred}`,
      form.message && `\nNotes:\n${form.message}`,
    ]
      .filter(Boolean)
      .join('\n');
    window.location.href = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <div className="lp">
      {/* NAV */}
      <nav className="top">
        <div className="inner">
          <Link className="logo" to="/"><img src={wordmark} alt="Porrada" /></Link>
          <div className="nav-cta">
            <Link className="btn btn-ghost" to="/login">Sign in</Link>
          </div>
        </div>
      </nav>

      <section className="demo">
        <div className="container">
          <span className="label section-eyebrow">— Book a demo</span>
          <h1>See Porrada<br />in action.</h1>
          <p className="section-sub">
            Tell us a little about your gym and we'll line up a 30-minute walkthrough —
            bookings, check-ins, payments, the lot.
          </p>

          <div className="demo-grid">
            {submitted ? (
              <div className="demo-success">
                <span className="label">— Request drafted</span>
                <h3>One more step.</h3>
                <p>
                  Your email app should have opened with your demo request ready to send.
                  Hit send and we'll get back to you within one business day.
                </p>
                <p>
                  Nothing opened? Email us directly at{' '}
                  <a href={`mailto:${DEMO_EMAIL}`}>{DEMO_EMAIL}</a>.
                </p>
                <button className="btn btn-ghost" onClick={() => setSubmitted(false)}>Edit my request</button>
              </div>
            ) : (
              <form className="demo-form" onSubmit={handleSubmit}>
                <div className="demo-row2">
                  <label>
                    Your name
                    <input type="text" value={form.name} onChange={set('name')} placeholder="Alex Morgan" required />
                  </label>
                  <label>
                    Email
                    <input type="email" value={form.email} onChange={set('email')} placeholder="alex@yourgym.com" required />
                  </label>
                </div>
                <div className="demo-row2">
                  <label>
                    Gym / studio name
                    <input type="text" value={form.gym} onChange={set('gym')} placeholder="Northside BJJ" required />
                  </label>
                  <label>
                    <span>Phone <span className="demo-opt">· optional</span></span>
                    <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+66 ..." />
                  </label>
                </div>
                <label>
                  <span>Preferred day &amp; time <span className="demo-opt">· optional</span></span>
                  <input type="text" value={form.preferred} onChange={set('preferred')} placeholder="Weekdays after 2pm" />
                </label>
                <label>
                  <span>Anything you want us to cover? <span className="demo-opt">· optional</span></span>
                  <textarea
                    value={form.message}
                    onChange={set('message')}
                    placeholder="Two locations, ~180 members, currently on spreadsheets…"
                  />
                </label>
                <button className="btn btn-primary btn-lg" type="submit">Book a Demo <span className="kbd">↵</span></button>
              </form>
            )}

            <aside className="demo-aside">
              <h4 className="label">— What to expect</h4>
              <ul>
                {expectations.map((item) => (
                  <li key={item.num}>
                    <span className="demo-num">{item.num}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="demo-alt">
                Prefer email? Write to us at <a href={`mailto:${DEMO_EMAIL}`}>{DEMO_EMAIL}</a>.
              </p>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
